import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import "./Sidebar.css";
import "font-awesome/css/font-awesome.min.css";
import { useAuth } from "../common/AuthContext"; // ✅ correct path

const Sidebar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const sidebarRef = useRef();
  const menuRef = useRef();
  const navigate = useNavigate();
  const location = useLocation();

  const { user, logout, getUserPlan, isPlanLoading } = useAuth();

  const isLoggedIn = !!user; // ✅ safe check

  const toggleSidebar = () => setIsOpen((current) => !current);

  const closeSidebar = () => {
    setIsOpen(false);
    setActiveDropdown(null);
  };

  const toggleDropdown = (index) => {
    setActiveDropdown(activeDropdown === index ? null : index);
  };

  const handleLogout = () => {
    logout();
    closeSidebar();
    navigate("/login");
  };

  const navigateTo = (path) => {
    navigate(path);
  };

  const checkUserPlanAndNavigate = (path) => {
    if (isPlanLoading) return;

    const userPlan = getUserPlan();

    if (userPlan) {
      navigate(path);
    } else {
      navigate("/subscribe");
    }

  };

  // ✅ Hook always runs (no conditional issue)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        menuRef.current &&
        !menuRef.current.contains(event.target)
      ) {
        closeSidebar();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  return isLoggedIn ? (
    <>
      {/* Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            onClick={closeSidebar}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <div
        className={`sidenav ${isOpen ? "open" : ""}`}
        ref={sidebarRef}
      >
        {/* Logo */}
        <div className="sidebar-logo">
          <img
            src="/assets/images/noptionwave-logo-1.png"
            className="logo-image"
            alt="logo"
          />
        </div>

        <div className="sidebar-menu">
          {/* Dashboard */}
          <a href="#" onClick={(e) => { e.preventDefault(); navigateTo("/dashboard"); }}>
            <i className="fa fa-info-circle"></i> DASHBOARD
          </a>

          {/* NSE DATA */}
          <div
            className={`dropdown-wrapper ${activeDropdown === 2 ? "active" : ""}`}
          >
            <button className="dropdown-btn" onClick={() => toggleDropdown(2)}>
              <i className="fa fa-bar-chart"></i> NSE DATA{" "}
              <i className="fa fa-caret-down"></i>
            </button>

            <AnimatePresence>
              {activeDropdown === 2 && (
                <motion.div className="dropdown-container">
                  <a href="#" onClick={(e) => { e.preventDefault(); checkUserPlanAndNavigate("/nse-option-chain"); }}>
                    <i className="fa fa-line-chart"></i> NSE OPTION CHAIN
                  </a>

                  <a href="#" onClick={(e) => { e.preventDefault(); checkUserPlanAndNavigate("/fifth-strike-option-chain"); }}>
                    <i className="fa fa-line-chart"></i> 5TH STRIKE DATA
                  </a>

                  {/* <a href="#" onClick={(e) => { e.preventDefault(); checkUserPlanAndNavigate("/day-range-analysis"); }}>
                    <i className="fa fa-line-chart"></i> DAY RANGE ANALYSIS
                  </a> */}

                  {user?.admin && user?.superAdmin && (
                    <a href="#" onClick={(e) => { e.preventDefault(); checkUserPlanAndNavigate("/oi-data-analysis"); }}>
                      <i className="fa fa-line-chart"></i> OI DATA ANALYSIS
                    </a>
                  )}

                  {user?.admin && user?.superAdmin && (
                    <a href="#" onClick={(e) => { e.preventDefault(); checkUserPlanAndNavigate("/option-chain-analysis"); }}>
                      <i className="fa fa-line-chart"></i> OPTION CHAIN ANALYSIS
                    </a>
                  )}

                  <a href="#" onClick={(e) => { e.preventDefault(); checkUserPlanAndNavigate("/greeks"); }}>
                    <i className="fa fa-line-chart"></i> GREEKS
                  </a>

                  {user?.admin && user?.superAdmin && (
                    <a href="#" onClick={(e) => { e.preventDefault(); checkUserPlanAndNavigate("/greeks-analyser"); }}>
                      <i className="fa fa-area-chart"></i> GREEKS ANALYSER
                    </a>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* ALGO TRADE */}
          {user?.admin && user?.superAdmin && (
            <div
              className={`dropdown-wrapper ${activeDropdown === 4 ? "active" : ""}`}
            >
              <button className="dropdown-btn" onClick={() => toggleDropdown(4)}>
                <i className="fa fa-cogs"></i> ALGO TRADE{" "}
                <i className="fa fa-caret-down"></i>
              </button>

              <AnimatePresence>
                {activeDropdown === 4 && (
                  <motion.div className="dropdown-container">
                    <a href="#" onClick={(e) => { e.preventDefault(); checkUserPlanAndNavigate("/algo-dashboard"); }}>
                      <i className="fa fa-dashboard"></i> ALGO DASHBOARD
                    </a>

                    <a href="#" onClick={(e) => { e.preventDefault(); checkUserPlanAndNavigate("/brokers"); }}>
                      <i className="fa fa-briefcase"></i> BROKERS
                    </a>
                    <a href="#" onClick={(e) => { e.preventDefault(); checkUserPlanAndNavigate("/strategies"); }}>
                      <i className="fa fa-bolt"></i> STRATEGIES
                    </a>
                    <a href="#" onClick={(e) => { e.preventDefault(); checkUserPlanAndNavigate("/strategies/pod-sector"); }}>
                      <i className="fa fa-filter"></i> POD STRATEGY
                    </a>
                    <a href="#" onClick={(e) => { e.preventDefault(); checkUserPlanAndNavigate("/orders"); }}>
                      <i className="fa fa-list-alt"></i> ORDERS
                    </a>
                    <a href="#" onClick={(e) => { e.preventDefault(); checkUserPlanAndNavigate("/positions"); }}>
                      <i className="fa fa-briefcase"></i> POSITIONS
                    </a>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {/* STOCK DATA */}
          <div
            className={`dropdown-wrapper ${activeDropdown === 3 ? "active" : ""}`}
          >
            <button className="dropdown-btn" onClick={() => toggleDropdown(3)}>
              <i className="fa fa-bar-chart"></i> STOCK DATA{" "}
              <i className="fa fa-caret-down"></i>
            </button>

            <AnimatePresence>
              {activeDropdown === 3 && (
                <motion.div className="dropdown-container">
                  <a href="#" onClick={(e) => { e.preventDefault(); checkUserPlanAndNavigate("/stock-data-analysis"); }}>
                    <i className="fa fa-line-chart"></i> STOCK DATA ANALYSIS
                  </a>

                  {user?.admin && user?.superAdmin && (
                    <a href="/stock-ltp-analysis" onClick={(e) => { e.preventDefault(); checkUserPlanAndNavigate("/stock-ltp-analysis"); }}>
                      <i className="fa fa-line-chart"></i> STOCK LTP ANALYSIS
                    </a>
                  )}

                  <a href="#" onClick={(e) => { e.preventDefault(); checkUserPlanAndNavigate("/stock-sector-analysis"); }}>
                    <i className="fa fa-line-chart"></i> STOCK SECTOR ANALYSIS
                  </a>

                  {user?.admin && (
                    <a href="#" onClick={(e) => { e.preventDefault(); checkUserPlanAndNavigate("/sectorial-indices"); }}>
                      <i className="fa fa-line-chart"></i> SECTORIAL INDICES
                    </a>
                  )}

                  {user?.admin && user?.superAdmin && (
                    <a href="#" onClick={(e) => { e.preventDefault(); checkUserPlanAndNavigate("/block-deals"); }}>
                      <i className="fa fa-line-chart"></i> BLOCK DEALS
                    </a>
                  )}

                  {user?.admin && user?.superAdmin && (
                    <a href="#" onClick={(e) => { e.preventDefault(); checkUserPlanAndNavigate("/buyer-seller-stock"); }}>
                      <i className="fa fa-line-chart"></i> BUY SELL STOCK
                    </a>
                  )}

                  {user?.admin && user?.superAdmin && (
                    <a href="#" onClick={(e) => { e.preventDefault(); checkUserPlanAndNavigate("/stock-scanner"); }}>
                      <i className="fa fa-line-chart"></i> STOCK SCANNER
                    </a>
                  )}

                  {/* {user?.admin && user?.superAdmin && (
                  <a href="#" onClick={(e) => { e.preventDefault(); checkUserPlanAndNavigate("/logical-indicator-value"); }}>
                    <i className="fa fa-line-chart"></i> LOGICAL INDICATOR
                  </a>
                )} */}

                  {user?.admin && user?.superAdmin && (
                    <a href="#" onClick={(e) => { e.preventDefault(); checkUserPlanAndNavigate("/positional"); }}>
                      <i className="fa fa-line-chart"></i> POSITIONAL
                    </a>
                  )}

                  {user?.admin && user?.superAdmin && (
                    <a href="#" onClick={(e) => { e.preventDefault(); checkUserPlanAndNavigate("/sector-performance"); }}>
                      <i className="fa fa-line-chart"></i> SECTOR PERFORMANCE
                    </a>
                  )}

                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* CRYPTO */}
          {/* <div
          className={`dropdown-wrapper ${activeDropdown === 1 ? "active" : ""}`}
          onMouseEnter={() => !isTouchDevice && setActiveDropdown(1)}
          onMouseLeave={() => !isTouchDevice && setActiveDropdown(null)}
          onClick={() => isTouchDevice && toggleDropdown(1)}
        >
          <button className="dropdown-btn">
            <i className="fa fa-bar-chart"></i> CRYPTO DATA{" "}
            <i className="fa fa-caret-down"></i>
          </button>

          <AnimatePresence>
            {activeDropdown === 1 && (
              <motion.div className="dropdown-container">
                <a href="#" onClick={(e) => { e.preventDefault(); checkUserPlanAndNavigate("/crypto-option-chain"); }}>
                  <i className="fa fa-line-chart"></i> CRYPTO OPTION CHAIN
                </a>
              </motion.div>
            )}
          </AnimatePresence>
        </div> */}
          {/* Other Links */}

          {user?.admin && user?.superAdmin && (
            <a href="#" onClick={(e) => { e.preventDefault(); checkUserPlanAndNavigate("/result"); }}>
              <i className="fa fa-line-chart"></i> RESULT
            </a>
          )}
          <a href="#" onClick={(e) => { e.preventDefault(); navigateTo("/global-data"); }}>
            <i className="fa fa-bar-chart"></i> GLOBAL DATA
          </a>

          <a href="#" onClick={(e) => { e.preventDefault(); navigateTo("/fii-dii"); }}>
            <i className="fa fa-bar-chart"></i> FII/DII DATA
          </a>

          {user?.admin && user?.superAdmin && (
            <a href="#" onClick={(e) => { e.preventDefault(); navigateTo("/fyers-access-token"); }}>
              <i className="fa fa-key"></i> Generate Access Token
            </a>
          )}

          {/* Other Links */}
          <a href="#" onClick={(e) => { e.preventDefault(); navigateTo("/subscribe"); }}>
            <i className="fa fa-rupee"></i> Subscribe
          </a>
        </div>

        {/* User */}
        <div className="user-profile">
          <div className="user-info">
            <i className="fa fa-user-circle fa-2x"></i>
            <div className="user-text">
              <strong>{user?.userName || "--"}</strong>
              <small>{user?.email || "--"}</small>
            </div>
          </div>

          <div className="user-actions">
            <a href="#" className="sidebar-profile-link" onClick={(e) => { e.preventDefault(); navigateTo("/profile"); }}>
              <i className="fa fa-user-circle" aria-hidden="true"></i>
              Profile
            </a>
            <button className="logout-btn" onClick={handleLogout} aria-label="Logout">
              <i className="fa fa-sign-out" aria-hidden="true"></i>
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Menu Button */}
      <button
        className="openbtn"
        ref={menuRef}
        onClick={toggleSidebar}
        aria-label={isOpen ? "Close navigation menu" : "Open navigation menu"}
        aria-expanded={isOpen}
      >
        ☰
      </button>
    </>
  ) : null;
};

export default Sidebar;
